import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import api from '../../utils/api';
import { useAuth } from '../../App';
import MidTermSection from './MidTermSection';
import {
  ArrowLeft, Save, Loader2, Image as ImageIcon, Plus, Trash2, Star, StarOff,
  ChevronUp, ChevronDown, Pencil, MapPin, Eye, Building2, CalendarRange, DownloadCloud, X, Sparkles, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import NewsletterCampaignDialog from '../../components/NewsletterCampaignDialog';

const eur = (v) => (v === null || v === undefined || v === '') ? '-' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return d; } };

const TABS = [
  { id: 'presentazione', label: 'Presentazione' },
  { id: 'foto', label: 'Foto' },
  { id: 'servizi', label: 'Servizi' },
  { id: 'spazi', label: 'Spazi & Posizione' },
  { id: 'regole', label: 'Regole' },
  { id: 'costi', label: 'Costi & Condizioni' },
  { id: 'listini', label: 'Listini' },
];

const PROPERTY_TYPES = ['Appartamento', 'Villa', 'Casa vacanze', 'Attico', 'Monolocale', 'Bilocale', 'Trilocale', 'Villetta a schiera', 'Rustico', 'Loft', 'B&B', 'Altro'];
const SOURCE_LABELS = {
  manual: { label: 'Manuale', cls: 'bg-emerald-100 text-emerald-700' },
  auto_max: { label: 'Auto prezzo max', cls: 'bg-amber-100 text-amber-700' },
  auto_annual: { label: 'Auto annuale', cls: 'bg-slate-100 text-slate-600' },
  auto_shift: { label: 'Generato', cls: 'bg-blue-100 text-blue-700' },
  discount: { label: 'Sconto', cls: 'bg-red-100 text-red-700' },
  imported: { label: 'Importato', cls: 'bg-blue-100 text-blue-700' },
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);
const TextInput = (props) => <input type="text" {...props} className={`w-full px-3 py-2 border rounded-lg text-sm ${props.className || ''}`} />;
const NumInput = (props) => <input type="number" step="0.01" {...props} className={`w-full px-3 py-2 border rounded-lg text-sm ${props.className || ''}`} />;
const TextArea = (props) => <textarea {...props} className={`w-full px-3 py-2 border rounded-lg text-sm ${props.className || ''}`} />;

const EMPTY_PERIOD = { arrival: '', departure: '', daily_price: '', paid_to_owner: '', markup: '', pm_booking: '', pm_airbnb: '', pm_expedia: '', min_stay: '', min_advance: '', band: '', source: 'manual' };

const RULE_FLAGS = [
  ['parties_forbidden', 'Vietato organizzare feste'],
  ['no_smoking', 'Vietato fumare'],
  ['pets_allowed', 'Animali ammessi'],
  ['pets_on_request', 'Animali ammessi su richiesta'],
  ['children_welcome', 'Bambini benvenuti'],
  ['young_groups_forbidden', 'Gruppi di giovani non ammessi'],
];

function CompletenessBar({ completeness }) {
  const [showMissing, setShowMissing] = useState(false);
  if (!completeness) return null;
  const pct = completeness.percent ?? 0;
  const color = pct < 75 ? 'bg-red-500' : pct < 90 ? 'bg-orange-500' : 'bg-emerald-500';
  const textColor = pct < 75 ? 'text-red-600' : pct < 90 ? 'text-orange-600' : 'text-emerald-600';
  return (
    <div className="w-full" data-testid="scheda-completeness">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600">Completezza pubblicazione</span>
        <button onClick={() => setShowMissing(!showMissing)}
          className={`text-xs font-bold ${textColor} hover:underline flex items-center gap-1`}
          data-testid="scheda-completeness-toggle">
          {pct}% ({completeness.done}/{completeness.total})
          {(completeness.missing || []).length > 0 && (showMissing ? '▲' : '▼')}
        </button>
      </div>
      <div className="h-2.5 w-full bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} data-testid="scheda-completeness-fill" />
      </div>
      {showMissing && (completeness.missing || []).length > 0 && (
        <div className="mt-2 bg-slate-50 border rounded-lg p-2.5" data-testid="scheda-completeness-missing">
          <p className="text-xs font-semibold text-slate-700 mb-1">Da completare:</p>
          <ul className="text-xs text-slate-600 space-y-0.5">
            {completeness.missing.map((m, i) => <li key={i} className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-red-400 inline-block" />{m}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}


export default function PropertySchedaPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'customer_service';
  const canMidTerm = user?.role === 'admin' || (user?.email || '').toLowerCase() === 'prenotazioni@holidayw.eu';
  const tabs = canMidTerm ? [...TABS, { id: 'mediotermine', label: 'Medio Termine' }] : TABS;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [pub, setPub] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [activeTab, setActiveTab] = useState('presentazione');
  const [previewDesc, setPreviewDesc] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCategory, setNewPhotoCategory] = useState('casa');
  const [completeness, setCompleteness] = useState(null);
  const [importJob, setImportJob] = useState(null);
  const [nlOpen, setNlOpen] = useState(false);
  const [importedTick, setImportedTick] = useState(0);
  const importPollRef = React.useRef(null);

  // Listini state
  const [periods, setPeriods] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const [plLoading, setPlLoading] = useState(false);
  const [periodDialog, setPeriodDialog] = useState(false);
  const [editPeriod, setEditPeriod] = useState(null);
  const [periodForm, setPeriodForm] = useState(EMPTY_PERIOD);
  const [periodSaving, setPeriodSaving] = useState(false);

  const setPubField = (k, v) => { setPub(p => ({ ...p, [k]: v })); setDirty(true); };
  const setNested = (group, k, v) => { setPub(p => ({ ...p, [group]: { ...(p[group] || {}), [k]: v } })); setDirty(true); };

  useEffect(() => {
    (async () => {
      try {
        const [pubRes, catRes] = await Promise.all([
          api.get(`/properties/${propertyId}/publication`),
          api.get('/publication/amenities-catalog'),
        ]);
        setProperty(pubRes.data.property);
        setPub(pubRes.data.publication);
        setCompleteness(pubRes.data.completeness || null);
        setCatalog(catRes.data.catalog || []);
      } catch {
        toast.error('Errore nel caricamento della scheda');
      } finally {
        setLoading(false);
      }
    })();
  }, [propertyId]);

  const reloadPublication = useCallback(async () => {
    try {
      const res = await api.get(`/properties/${propertyId}/publication`);
      if (res.data.property) setProperty(res.data.property);
      setPub(res.data.publication);
      setCompleteness(res.data.completeness || null);
      setDirty(false);
    } catch { /* silente */ }
  }, [propertyId]);

  const stopImportPoll = () => { if (importPollRef.current) { clearInterval(importPollRef.current); importPollRef.current = null; } };

  const pollImportStatus = useCallback(() => {
    stopImportPoll();
    importPollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/properties/${propertyId}/import-status`);
        const job = res.data.job;
        setImportJob(job);
        if (job && job.status !== 'running') {
          stopImportPoll();
          if (job.status === 'completed') {
            const s = job.stats || {};
            toast.success(`Importazione completata: ${s.photos_uploaded || 0} foto, ${s.pricelists_imported || 0} periodi listino, ${s.services_mapped || 0} servizi`, { duration: 8000 });
            reloadPublication();
            setImportedTick(t => t + 1);
          } else {
            toast.error(`Importazione fallita: ${job.error || 'errore sconosciuto'}`, { duration: 10000 });
          }
        }
      } catch { /* riprova al prossimo tick */ }
    }, 2000);
  }, [propertyId, reloadPublication]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/properties/${propertyId}/import-status`);
        if (res.data.job?.status === 'running') { setImportJob(res.data.job); pollImportStatus(); }
      } catch { /* silente */ }
    })();
    return stopImportPoll;
  }, [propertyId, pollImportStatus]);

  const handleStartImport = async () => {
    if (!window.confirm('Importare la scheda dal vecchio sito? Descrizione, servizi, regole, costi, FOTO e LISTINI verranno sovrascritti con i dati del vecchio sito.')) return;
    try {
      await api.post(`/properties/${propertyId}/import-from-old-site`, {});
      setImportJob({ status: 'running', step: 'Avvio...' });
      toast.info('Importazione avviata...');
      pollImportStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio importazione');
    }
  };

  const refreshCompleteness = useCallback(async () => {
    try {
      const res = await api.get(`/properties/${propertyId}/publication`);
      setCompleteness(res.data.completeness || null);
    } catch { /* silente */ }
  }, [propertyId]);

  const fetchPricelists = useCallback(async () => {
    setPlLoading(true);
    try {
      const res = await api.get(`/properties/${propertyId}/pricelists?year=${plYear}`);
      setPeriods(res.data.periods || []);
      setAvailableYears(res.data.available_years || []);
    } catch { toast.error('Errore caricamento listini'); }
    finally { setPlLoading(false); }
  }, [propertyId, plYear]);
  useEffect(() => { fetchPricelists(); }, [fetchPricelists]);
  useEffect(() => { if (importedTick > 0) fetchPricelists(); }, [importedTick, fetchPricelists]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...pub };
      delete payload.updated_at; delete payload.updated_by;
      const res = await api.put(`/properties/${propertyId}/publication`, payload);
      if (res.data.completeness) setCompleteness(res.data.completeness);
      toast.success('Scheda salvata');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const handleGenerateAI = async () => {
    setAiLoading(true);
    try {
      const res = await api.post(`/properties/${propertyId}/generate-ai-description`, {}, { timeout: 180000 });
      setPub(p => ({ ...p, description_ai: res.data.description_ai }));
      toast.success('Descrizione AI generata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione AI');
    } finally { setAiLoading(false); }
  };
  const useAiAsMain = () => {
    setPubField('description_html', pub.description_ai);
    toast.info('Descrizione AI copiata come descrizione principale — ricordati di salvare la scheda');
  };

  // ─── Gallery helpers ───
  const gallery = pub?.gallery || [];
  const addPhoto = () => {
    const url = newPhotoUrl.trim();
    if (!url) return;
    setPubField('gallery', [...gallery, { url, caption: '', order: gallery.length + 1, is_cover: gallery.length === 0, category: newPhotoCategory }]);
    setNewPhotoUrl('');
  };
  const togglePhotoCategory = (i) => setPubField('gallery', gallery.map((g, idx) => idx === i ? { ...g, category: (g.category || 'casa') === 'casa' ? 'zona' : 'casa' } : g));
  const removePhoto = (i) => setPubField('gallery', gallery.filter((_, idx) => idx !== i));
  const setCover = (i) => setPubField('gallery', gallery.map((g, idx) => ({ ...g, is_cover: idx === i })));
  const movePhoto = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= gallery.length) return;
    const next = [...gallery];
    [next[i], next[j]] = [next[j], next[i]];
    setPubField('gallery', next.map((g, idx) => ({ ...g, order: idx + 1 })));
  };

  const toggleService = (key) => {
    const cur = pub?.services || [];
    setPubField('services', cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);
  };

  // ─── Pricelist handlers ───
  const openPeriodDialog = (period = null) => {
    setEditPeriod(period);
    setPeriodForm(period ? {
      arrival: period.arrival, departure: period.departure,
      daily_price: period.daily_price ?? '', paid_to_owner: period.paid_to_owner ?? '',
      markup: period.markup ?? (period.platform_markups?.hw ?? ''),
      pm_booking: period.platform_markups?.booking ?? '',
      pm_airbnb: period.platform_markups?.airbnb ?? '',
      pm_expedia: period.platform_markups?.expedia ?? '',
      min_stay: period.min_stay ?? '',
      min_advance: period.min_advance || '', band: period.band || '', source: period.source || 'manual',
    } : { ...EMPTY_PERIOD });
    setPeriodDialog(true);
  };
  const updatePeriodCalc = (field, value) => {
    setPeriodForm(f => {
      const next = { ...f, [field]: value };
      const owner = parseFloat(next.paid_to_owner);
      const pct = parseFloat(next.markup);
      if (!isNaN(owner) && !isNaN(pct)) {
        next.daily_price = String(Math.round(owner * (1 + pct / 100) * 100) / 100);
      }
      return next;
    });
  };
  const platformPrice = (pm) => {
    const owner = parseFloat(periodForm.paid_to_owner);
    const pct = parseFloat(pm);
    if (isNaN(owner) || isNaN(pct)) return null;
    return (owner * (1 + pct / 100)).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  };
  const savePeriod = async (e) => {
    e.preventDefault();
    setPeriodSaving(true);
    try {
      const payload = {
        arrival: periodForm.arrival, departure: periodForm.departure,
        daily_price: Number(periodForm.daily_price),
        paid_to_owner: periodForm.paid_to_owner !== '' ? Number(periodForm.paid_to_owner) : null,
        markup: periodForm.markup !== '' ? Number(periodForm.markup) : null,
        platform_markups: (() => {
          const pm = {};
          if (periodForm.markup !== '') pm.hw = Number(periodForm.markup);
          [['booking', periodForm.pm_booking], ['airbnb', periodForm.pm_airbnb], ['expedia', periodForm.pm_expedia]]
            .forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) pm[k] = Number(v); });
          return pm;
        })(),
        min_stay: periodForm.min_stay !== '' ? Number(periodForm.min_stay) : null,
        min_advance: periodForm.min_advance || null,
        band: periodForm.band || null,
        source: periodForm.source,
      };
      if (editPeriod) await api.put(`/pricelists/${editPeriod.id}`, payload);
      else await api.post(`/properties/${propertyId}/pricelists`, payload);
      toast.success(editPeriod ? 'Periodo aggiornato' : 'Periodo aggiunto');
      setPeriodDialog(false); setEditPeriod(null);
      fetchPricelists();
      refreshCompleteness();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio del periodo');
    } finally { setPeriodSaving(false); }
  };
  const deletePeriod = async (p) => {
    if (!window.confirm(`Eliminare il periodo ${fmtDate(p.arrival)} → ${fmtDate(p.departure)}?`)) return;
    try { await api.delete(`/pricelists/${p.id}`); toast.success('Periodo eliminato'); fetchPricelists(); refreshCompleteness(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const plTotals = useMemo(() => ({
    count: periods.length,
    avg: periods.length ? periods.reduce((s, p) => s + Number(p.daily_price || 0), 0) / periods.length : 0,
  }), [periods]);

  if (loading) return <Layout title="Scheda Immobile"><div className="text-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div></Layout>;
  if (!property) return <Layout title="Scheda Immobile"><Card className="p-8 text-center text-slate-500">Immobile non trovato</Card></Layout>;

  return (
    <Layout title={`Scheda — ${property.name}`}>
      <div className="space-y-4" data-testid="property-scheda-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/properties')} data-testid="scheda-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Immobili
            </Button>
            <div>
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" /> {property.name}
              </h2>
              <p className="text-xs text-slate-500">
                {property.property_code} · {property.city} ({property.province}) · {property.region}
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-white" title="Rende l'immobile visibile e prenotabile sul sito Holidayworld">
                <span className="text-xs font-medium text-slate-600">Sito Holidayworld</span>
                <Switch checked={!!property.site_published} data-testid="site-publish-toggle"
                  onCheckedChange={async (v) => {
                    try {
                      await api.post(`/properties/${propertyId}/site-publish`, { published: v });
                      setProperty(p => ({ ...p, site_published: v }));
                      toast.success(v ? 'Immobile pubblicato sul sito Holidayworld' : 'Immobile rimosso dal sito Holidayworld');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Errore aggiornamento pubblicazione sito', { duration: 10000 });
                    }
                  }} />
              </div>
              <Button variant="outline" onClick={handleStartImport} disabled={importJob?.status === 'running'}
                className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="scheda-import-btn">
                {importJob?.status === 'running'
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {importJob.step || 'Importazione...'}</>
                  : <><DownloadCloud className="h-4 w-4 mr-1" /> Importa dal vecchio sito</>}
              </Button>
              <Button variant="outline" onClick={() => setNlOpen(true)}
                className="border-blue-800 text-blue-900 hover:bg-blue-50" data-testid="scheda-newsletter-btn">
                <Mail className="h-4 w-4 mr-1" /> Crea Newsletter
              </Button>
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="scheda-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {dirty ? 'Salva scheda' : 'Salvato'}
              </Button>
            </div>
          )}
        </div>

        <NewsletterCampaignDialog
          property={property}
          open={nlOpen}
          onOpenChange={setNlOpen}
          onLinkSaved={(url) => setProperty((p) => ({ ...p, holidayworld_url: url }))}
        />

        {/* Barra completezza pubblicazione */}
        <Card className="p-4">
          <CompletenessBar completeness={completeness} />
        </Card>

        {/* Conflitti import vs campi manuali */}
        {(pub?.import_conflicts || []).length > 0 && (
          <Card className="p-4 border border-amber-300 bg-amber-50" data-testid="scheda-import-conflicts">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  ⚠️ L'ultimo import dal vecchio sito aveva valori diversi sui campi che hai modificato a mano — sono stati MANTENUTI i tuoi valori
                </p>
                <ul className="mt-1.5 text-xs text-amber-700 space-y-0.5">
                  {pub.import_conflicts.map((c, i) => (
                    <li key={i} data-testid={`scheda-conflict-${c.field}`}>
                      <b>{c.label}</b>: HWsmart «{c.hwsmart}» · vecchio sito «{c.old_site}»
                    </li>
                  ))}
                </ul>
              </div>
              <Button variant="ghost" size="sm" className="text-amber-700 hover:bg-amber-100 shrink-0"
                data-testid="scheda-conflicts-hide"
                onClick={async () => {
                  try {
                    await api.post(`/properties/${propertyId}/publication/clear-conflicts`);
                    reloadPublication();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Errore');
                  }
                }}>
                Nascondi
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="scheda-tabs">
          {tabs.map(t => (
            <button key={t.id} data-testid={`scheda-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
              className={`flex-1 whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ PRESENTAZIONE ═══ */}
        {activeTab === 'presentazione' && (
          <Card className="p-5 space-y-4" data-testid="scheda-presentazione">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Tipologia di alloggio">
                <select value={pub.property_type_display || ''} onChange={(e) => setPubField('property_type_display', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="scheda-tipologia">
                  <option value="">Seleziona...</option>
                  {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Posizione / Destinazione">
                <TextInput value={pub.position || ''} onChange={(e) => setPubField('position', e.target.value)}
                  placeholder="Es. Genova (GE) - Liguria" data-testid="scheda-posizione" />
              </Field>
              <Field label="Mostra in home page">
                <div className="flex items-center h-10">
                  <Switch checked={!!pub.show_on_homepage} onCheckedChange={(v) => setPubField('show_on_homepage', v)} data-testid="scheda-show-home" />
                  <span className="ml-2 text-sm text-slate-600">{pub.show_on_homepage ? 'Sì' : 'No'}</span>
                </div>
              </Field>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-600">Descrizione (HTML supportato)</label>
                <button onClick={() => setPreviewDesc(!previewDesc)} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="scheda-desc-preview-toggle">
                  <Eye className="h-3 w-3" /> {previewDesc ? 'Modifica' : 'Anteprima'}
                </button>
              </div>
              {previewDesc ? (
                <div className="border rounded-lg p-4 min-h-[200px] prose prose-sm max-w-none text-sm"
                  dangerouslySetInnerHTML={{ __html: pub.description_html || '<em class="text-slate-400">Nessuna descrizione</em>' }} />
              ) : (
                <TextArea rows={12} value={pub.description_html || ''} onChange={(e) => setPubField('description_html', e.target.value)}
                  placeholder="Descrizione completa dell'alloggio..." data-testid="scheda-descrizione" />
              )}
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" /> Descrizione estesa generata con AI
                </label>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={handleGenerateAI} disabled={aiLoading}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="scheda-ai-generate-btn">
                    {aiLoading
                      ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generazione in corso (30-60s)...</>
                      : <><Sparkles className="h-4 w-4 mr-1" /> {pub.description_ai ? 'Rigenera con AI' : 'Genera con AI'}</>}
                  </Button>
                )}
              </div>
              {pub.description_ai ? (
                <div className="space-y-2">
                  <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-4 prose prose-sm max-w-none text-sm max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: pub.description_ai }} data-testid="scheda-ai-desc-preview" />
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={useAiAsMain} data-testid="scheda-ai-use-btn">
                      Usa come descrizione principale
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400" data-testid="scheda-ai-desc-empty">
                  Nessuna descrizione AI — genera una proposta più lunga e orientata alla vendita basata sui dati della scheda (località, servizi, regole). Salva prima la scheda per usare i dati aggiornati.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* ═══ FOTO ═══ */}
        {activeTab === 'foto' && (() => {
          const casaCount = gallery.filter(g => (g.category || 'casa') === 'casa').length;
          const zonaCount = gallery.filter(g => g.category === 'zona').length;
          const cntCls = (n, min) => n >= min ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700';
          return (
          <Card className="p-5 space-y-4" data-testid="scheda-foto">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                <ImageIcon className="h-4 w-4 text-blue-600" /> Galleria fotografica
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cntCls(casaCount, 15)}`} data-testid="scheda-foto-count-casa">Casa: {casaCount}/15</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cntCls(zonaCount, 10)}`} data-testid="scheda-foto-count-zona">Zona: {zonaCount}/10</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cntCls(gallery.length, 25)}`} data-testid="scheda-foto-count-totale">Totale: {gallery.length}/25</span>
              </h3>
              {canEdit && (
                <div className="flex gap-2">
                  <TextInput value={newPhotoUrl} onChange={(e) => setNewPhotoUrl(e.target.value)} placeholder="URL foto..." className="w-64" data-testid="scheda-photo-url-input" />
                  <select value={newPhotoCategory} onChange={(e) => setNewPhotoCategory(e.target.value)} className="px-2 py-2 border rounded-lg text-sm bg-white" data-testid="scheda-photo-category-select">
                    <option value="casa">Casa</option>
                    <option value="zona">Zona</option>
                  </select>
                  <Button size="sm" onClick={addPhoto} data-testid="scheda-photo-add-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500">Requisito minimo: <strong>15 foto della casa + 10 della zona</strong> (comunque non meno di 25). Clicca sull'etichetta Casa/Zona di una foto per cambiarne la categoria.</p>
            {gallery.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="scheda-foto-empty">
                <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                Nessuna foto — verranno importate dal vecchio sito oppure aggiungile tramite URL
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {gallery.map((g, i) => (
                  <div key={i} className={`relative group border rounded-lg overflow-hidden ${g.is_cover ? 'ring-2 ring-amber-400' : ''}`} data-testid={`scheda-photo-${i}`}>
                    <img src={g.url} alt={g.caption || `Foto ${i + 1}`} className="w-full h-32 object-cover" loading="lazy"
                      onError={(e) => { e.target.style.opacity = 0.3; }} />
                    {g.is_cover && <span className="absolute top-1 left-1 bg-amber-400 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">COPERTINA</span>}
                    <button onClick={() => canEdit && togglePhotoCategory(i)}
                      className={`absolute top-1 right-1 text-[10px] px-1.5 py-0.5 rounded-full font-bold ${(g.category || 'casa') === 'casa' ? 'bg-blue-500 text-white' : 'bg-teal-500 text-white'} ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                      title={canEdit ? 'Clicca per cambiare categoria' : ''} data-testid={`scheda-photo-category-${i}`}>
                      {(g.category || 'casa') === 'casa' ? 'CASA' : 'ZONA'}
                    </button>
                    {canEdit && (
                      <div className="absolute inset-x-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 py-1">
                        <button onClick={() => setCover(i)} className="p-1 text-white hover:text-amber-300" title={g.is_cover ? 'Copertina' : 'Imposta copertina'}>
                          {g.is_cover ? <Star className="h-3.5 w-3.5 fill-amber-300 text-amber-300" /> : <StarOff className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => movePhoto(i, -1)} className="p-1 text-white hover:text-blue-300" title="Sposta su"><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => movePhoto(i, 1)} className="p-1 text-white hover:text-blue-300" title="Sposta giù"><ChevronDown className="h-3.5 w-3.5" /></button>
                        <button onClick={() => removePhoto(i)} className="p-1 text-white hover:text-red-400" title="Rimuovi" data-testid={`scheda-photo-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          );
        })()}

        {/* ═══ SERVIZI ═══ */}
        {activeTab === 'servizi' && (
          <div className="space-y-4" data-testid="scheda-servizi">
            {(pub.services_extra || []).length > 0 && (
              <Card className="p-4 bg-amber-50 border-amber-200" data-testid="scheda-servizi-extra">
                <h3 className="text-sm font-bold text-amber-900 mb-2">Altri servizi importati dal vecchio sito (non in catalogo)</h3>
                <div className="flex flex-wrap gap-1.5">
                  {(pub.services_extra || []).map((label, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-900 text-xs px-2 py-1 rounded-full">
                      {label}
                      {canEdit && (
                        <button onClick={() => setPubField('services_extra', (pub.services_extra || []).filter((_, idx) => idx !== i))}
                          className="text-amber-500 hover:text-red-600" data-testid={`scheda-servizi-extra-remove-${i}`}>
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </Card>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {catalog.map(cat => (
              <Card key={cat.category} className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-2">{cat.category}</h3>
                <div className="space-y-1.5">
                  {cat.items.map(item => (
                    <label key={item.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                      <input type="checkbox" checked={(pub.services || []).includes(item.key)}
                        onChange={() => canEdit && toggleService(item.key)} disabled={!canEdit}
                        className="rounded border-slate-300" data-testid={`scheda-service-${item.key}`} />
                      {item.label}
                    </label>
                  ))}
                </div>
              </Card>
            ))}
            </div>
          </div>
        )}

        {/* ═══ SPAZI & POSIZIONE ═══ */}
        {activeTab === 'spazi' && (
          <div className="space-y-4" data-testid="scheda-spazi">
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Spazi</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Camere (da gestione)"><div className="px-3 py-2 bg-slate-50 rounded-lg text-sm">{property.bedrooms ?? '-'}</div></Field>
                <Field label="Bagni (da gestione)"><div className="px-3 py-2 bg-slate-50 rounded-lg text-sm">{property.bathrooms ?? '-'}</div></Field>
                <Field label="Ospiti max (da gestione)"><div className="px-3 py-2 bg-slate-50 rounded-lg text-sm">{property.max_guests ?? '-'}</div></Field>
                <Field label="Piano"><TextInput value={pub.floor || ''} onChange={(e) => setPubField('floor', e.target.value)} placeholder="Es. 2" data-testid="scheda-piano" /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Camere (descrizione)"><TextInput value={pub.bedrooms_description || ''} onChange={(e) => setPubField('bedrooms_description', e.target.value)} placeholder="Es. Una camera matrimoniale, una con due letti singoli" data-testid="scheda-bedrooms-desc" /></Field>
                <Field label="Bagni (descrizione)"><TextInput value={pub.bathrooms_description || ''} onChange={(e) => setPubField('bathrooms_description', e.target.value)} placeholder="Es. Due bagni con doccia e bidet" data-testid="scheda-bathrooms-desc" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {[['elevator', 'Ascensore'], ['living_room', 'Soggiorno'], ['kitchenette', 'Angolo cottura']].map(([k, label]) => (
                  <Field key={k} label={label}>
                    <div className="flex items-center h-10">
                      <Switch checked={!!pub[k]} onCheckedChange={(v) => setPubField(k, v)} data-testid={`scheda-${k}`} />
                      <span className="ml-2 text-sm text-slate-600">{pub[k] ? 'Sì' : 'No'}</span>
                    </div>
                  </Field>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" /> Posizione & Orari</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Latitudine"><NumInput step="0.000001" value={pub.latitude ?? ''} onChange={(e) => setPubField('latitude', e.target.value === '' ? null : Number(e.target.value))} placeholder="44.4056" data-testid="scheda-lat" /></Field>
                <Field label="Longitudine"><NumInput step="0.000001" value={pub.longitude ?? ''} onChange={(e) => setPubField('longitude', e.target.value === '' ? null : Number(e.target.value))} placeholder="8.9463" data-testid="scheda-lng" /></Field>
                <Field label="Orario arrivo (check-in)"><TextInput value={pub.checkin_time || ''} onChange={(e) => setPubField('checkin_time', e.target.value)} placeholder="16:00 - 20:00" data-testid="scheda-checkin" /></Field>
                <Field label="Orario partenza (check-out)"><TextInput value={pub.checkout_time || ''} onChange={(e) => setPubField('checkout_time', e.target.value)} placeholder="entro le 10:00" data-testid="scheda-checkout" /></Field>
              </div>
              <p className="text-xs text-slate-400 mt-3">Indirizzo: {property.address || '-'} — {property.postal_code} {property.city} ({property.province})</p>
            </Card>
          </div>
        )}

        {/* ═══ REGOLE ═══ */}
        {activeTab === 'regole' && (
          <div className="space-y-4" data-testid="scheda-regole">
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Regole della casa (checklist)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {RULE_FLAGS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-2 py-1.5 border">
                    <input type="checkbox" checked={!!pub.house_rules_flags?.[k]}
                      onChange={() => canEdit && setNested('house_rules_flags', k, !pub.house_rules_flags?.[k])}
                      disabled={!canEdit} className="rounded border-slate-300" data-testid={`scheda-rule-flag-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="mt-4">
                <Field label="Altre regole / note">
                  <TextArea rows={3} value={pub.house_rules_notes || ''} onChange={(e) => setPubField('house_rules_notes', e.target.value)}
                    placeholder="Es. Silenzio dalle 23:00 alle 8:00..." data-testid="scheda-rule-notes" />
                </Field>
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Dettagli per argomento (testo libero)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['children', 'Bambini'], ['pets', 'Animali'], ['smoking', 'Fumatori'], ['parties', 'Feste / Eventi'], ['internet', 'Internet'], ['parking', 'Parcheggio']].map(([k, label]) => (
                  <Field key={k} label={label}>
                    <TextArea rows={2} value={pub.house_rules?.[k] || ''} onChange={(e) => setNested('house_rules', k, e.target.value)}
                      placeholder={`Regole su ${label.toLowerCase()}...`} data-testid={`scheda-rule-${k}`} />
                  </Field>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ═══ COSTI & CONDIZIONI ═══ */}
        {activeTab === 'costi' && (
          <div className="space-y-4" data-testid="scheda-costi">
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Spese ospite</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Cauzione (€)"><NumInput value={pub.fees?.deposit ?? ''} onChange={(e) => setNested('fees', 'deposit', e.target.value === '' ? null : Number(e.target.value))} placeholder="200" data-testid="scheda-cauzione" /></Field>
                <Field label="Pulizie (€, da gestione)"><div className="px-3 py-2 bg-slate-50 rounded-lg text-sm">{eur(property.cleaning_cost)}{pub.fees?.cleaning != null && Number(pub.fees.cleaning) !== Number(property.cleaning_cost || 0) ? <span className="text-xs text-amber-600 block" data-testid="scheda-pulizie-vecchio-sito">Vecchio sito: {eur(pub.fees.cleaning)}{pub.fees?.cleaning_payment ? ` (${pub.fees.cleaning_payment})` : ''}</span> : null}</div></Field>
                <Field label="Consumi"><TextInput value={pub.fees?.utilities || ''} onChange={(e) => setNested('fees', 'utilities', e.target.value)} placeholder="Inclusi / a consumo..." data-testid="scheda-consumi" /></Field>
                <Field label="Tassa di soggiorno (nota)"><TextInput value={pub.fees?.tourist_tax_note || ''} onChange={(e) => setNested('fees', 'tourist_tax_note', e.target.value)} placeholder="Es. €1,50/notte adulti" data-testid="scheda-tassa-nota" /></Field>
              </div>
              <h4 className="text-xs font-bold text-slate-700 mt-4 mb-2">Biancheria & Pulizia</h4>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Importo (€)"><NumInput value={pub.fees?.linen_amount ?? ''} onChange={(e) => setNested('fees', 'linen_amount', e.target.value === '' ? null : Number(e.target.value))} placeholder="10" data-testid="scheda-biancheria-importo" /></Field>
                <Field label="Tipologia">
                  <select value={pub.fees?.linen_per || 'persona'} onChange={(e) => setNested('fees', 'linen_per', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="scheda-biancheria-per">
                    <option value="persona">A persona</option>
                    <option value="soggiorno">A soggiorno</option>
                  </select>
                </Field>
                <Field label="Pagamento">
                  <select value={pub.fees?.linen_payment || 'check-in'} onChange={(e) => setNested('fees', 'linen_payment', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="scheda-biancheria-pagamento">
                    <option value="prenotazione">Alla prenotazione</option>
                    <option value="check-in">Al check-in</option>
                  </select>
                </Field>
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Condizioni</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Condizioni di pagamento"><TextArea rows={4} value={pub.conditions?.payment || ''} onChange={(e) => setNested('conditions', 'payment', e.target.value)} data-testid="scheda-cond-pagamento" /></Field>
                <Field label="Politica di cancellazione"><TextArea rows={4} value={pub.conditions?.cancellation || ''} onChange={(e) => setNested('conditions', 'cancellation', e.target.value)} data-testid="scheda-cond-cancellazione" /></Field>
                <Field label="Altre condizioni"><TextArea rows={4} value={pub.conditions?.other || ''} onChange={(e) => setNested('conditions', 'other', e.target.value)} data-testid="scheda-cond-altro" /></Field>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ LISTINI ═══ */}
        {activeTab === 'listini' && (
          <Card className="p-5 space-y-3" data-testid="scheda-listini">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-blue-600" /> Listini prezzi per periodo
                <span className="text-xs font-normal text-slate-500">({plTotals.count} periodi · media {eur(plTotals.avg)}/notte)</span>
                {pub?.old_site_calendar && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5" data-testid="scheda-calendar-badge">
                    Calendario: {pub.old_site_calendar}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <select value={plYear} onChange={(e) => setPlYear(Number(e.target.value))} className="px-2 py-1.5 border rounded-lg text-sm bg-white" data-testid="scheda-listini-year">
                  {[...new Set([new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1, ...availableYears.map(Number)])].sort((a, b) => b - a).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {canEdit && pub?.old_site_calendar === 'Personalizzato' && (
                  <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="scheda-shift-generate-btn"
                    onClick={async () => {
                      if (!window.confirm(`Generare i listini ${plYear + 1} partendo dal ${plYear}?\n\nLe date verranno adattate automaticamente (stesso giorno della settimana, Pasqua e festività ricalcolate) e i prezzi copiati identici. Potrai poi modificare ogni periodo.`)) return;
                      try {
                        const res = await api.post('/pricelist-calendars/generate-personalizzati', { from_year: plYear, property_id: propertyId });
                        const r = res.data.properties?.[0];
                        if (r?.skipped) toast.warning(r.skipped, { duration: 8000 });
                        else toast.success(`${r?.created || 0} periodi ${plYear + 1} generati${(r?.warnings || []).length ? ` — ${r.warnings.length} avvisi da verificare` : ''}`, { duration: 8000 });
                        fetchPricelists();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Errore generazione', { duration: 8000 });
                      }
                    }}>
                    <CalendarRange className="h-4 w-4 mr-1" /> Genera {plYear + 1} dal {plYear}
                  </Button>
                )}
                {canEdit && <Button size="sm" onClick={() => openPeriodDialog()} data-testid="scheda-period-add-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi periodo</Button>}
              </div>
            </div>
            {plLoading ? (
              <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            ) : periods.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm" data-testid="scheda-listini-empty">
                <CalendarRange className="h-8 w-8 mx-auto mb-2" />
                Nessun periodo per il {plYear} — verranno importati dal vecchio sito oppure aggiungili manualmente
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left p-2 font-medium text-slate-600">Arrivo</th>
                    <th className="text-left p-2 font-medium text-slate-600">Partenza</th>
                    <th className="text-right p-2 font-medium text-slate-600">Prezzo/giorno</th>
                    <th className="text-right p-2 font-medium text-slate-600">Al proprietario</th>
                    <th className="text-right p-2 font-medium text-slate-600">Ricarico HW</th>
                    <th className="text-center p-2 font-medium text-slate-600">Altre piattaforme</th>
                    <th className="text-center p-2 font-medium text-slate-600">Sogg. min</th>
                    <th className="text-center p-2 font-medium text-slate-600">Anticipo</th>
                    <th className="text-center p-2 font-medium text-slate-600">Fascia</th>
                    <th className="text-center p-2 font-medium text-slate-600">Tipo</th>
                    <th className="text-right p-2"></th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {periods.map(p => {
                      const src = SOURCE_LABELS[p.source] || SOURCE_LABELS.manual;
                      return (
                        <tr key={p.id} className="hover:bg-slate-50" data-testid={`scheda-period-row-${p.id}`}>
                          <td className="p-2 whitespace-nowrap">{fmtDate(p.arrival)}</td>
                          <td className="p-2 whitespace-nowrap">{fmtDate(p.departure)}</td>
                          <td className="p-2 text-right font-semibold">{eur(p.daily_price)}</td>
                          <td className="p-2 text-right text-slate-600">{eur(p.paid_to_owner)}</td>
                          <td className="p-2 text-right text-slate-600">{p.markup !== null && p.markup !== undefined ? `${Number(p.markup).toLocaleString('it-IT')}%` : '-'}</td>
                          <td className="p-2 text-center">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {['booking', 'airbnb', 'expedia'].filter(k => p.platform_markups?.[k] !== null && p.platform_markups?.[k] !== undefined).map(k => (
                                <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium" title={k}>
                                  {k === 'booking' ? 'B' : k === 'airbnb' ? 'A' : 'E'}: {p.platform_markups[k]}%
                                </span>
                              ))}
                              {!['booking', 'airbnb', 'expedia'].some(k => p.platform_markups?.[k] !== null && p.platform_markups?.[k] !== undefined) && <span className="text-slate-300">-</span>}
                            </div>
                          </td>
                          <td className="p-2 text-center">{p.min_stay ?? '-'}</td>
                          <td className="p-2 text-center">{p.min_advance || '-'}</td>
                          <td className="p-2 text-center font-bold">{p.band || '-'}</td>
                          <td className="p-2 text-center"><span className={`px-2 py-0.5 rounded-full text-xs ${src.cls}`}>{src.label}</span></td>
                          <td className="p-2 text-right whitespace-nowrap">
                            {canEdit && (<>
                              <button onClick={() => openPeriodDialog(p)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Modifica" data-testid={`scheda-period-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                              <button onClick={() => deletePeriod(p)} className="p-1.5 text-slate-400 hover:text-red-600" title="Elimina" data-testid={`scheda-period-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* ═══ MEDIO TERMINE ═══ */}
        {activeTab === 'mediotermine' && canMidTerm && (
          <MidTermSection propertyId={propertyId} pub={pub} setPubField={setPubField} canEdit={canEdit} dirty={dirty} isAdmin={user?.role === 'admin'} />
        )}

        {/* Dialog periodo listino */}
        <Dialog open={periodDialog} onOpenChange={(o) => { if (!o) { setPeriodDialog(false); setEditPeriod(null); } }}>
          <DialogContent className="max-w-lg" data-testid="scheda-period-dialog">
            <DialogHeader>
              <DialogTitle>{editPeriod ? 'Modifica periodo' : 'Nuovo periodo di listino'}</DialogTitle>
              <DialogDescription>Prezzi per periodo di {property.name}</DialogDescription>
            </DialogHeader>
            <form onSubmit={savePeriod} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Arrivo *"><input type="date" value={periodForm.arrival} onChange={(e) => setPeriodForm(f => ({ ...f, arrival: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="period-form-arrival" /></Field>
                <Field label="Partenza *"><input type="date" value={periodForm.departure} onChange={(e) => setPeriodForm(f => ({ ...f, departure: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" required data-testid="period-form-departure" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Al proprietario (€)"><NumInput value={periodForm.paid_to_owner} onChange={(e) => updatePeriodCalc('paid_to_owner', e.target.value)} data-testid="period-form-owner" /></Field>
                <Field label="Ricarico HW (%)"><NumInput value={periodForm.markup} onChange={(e) => updatePeriodCalc('markup', e.target.value)} data-testid="period-form-markup" /></Field>
                <Field label="Prezzo/giorno (€) *"><NumInput value={periodForm.daily_price} onChange={(e) => setPeriodForm(f => ({ ...f, daily_price: e.target.value }))} required data-testid="period-form-price" /></Field>
              </div>
              <p className="text-[11px] text-slate-400 -mt-1">Il ricarico è una % sul prezzo al proprietario: Prezzo/giorno = Al proprietario × (1 + Ricarico%). Ricalcolato automaticamente, comunque modificabile.</p>
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Ricarichi altre piattaforme (% sul prezzo al proprietario)</p>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Booking.com (%)">
                    <NumInput value={periodForm.pm_booking} onChange={(e) => setPeriodForm(f => ({ ...f, pm_booking: e.target.value }))} data-testid="period-form-pm-booking" />
                    {platformPrice(periodForm.pm_booking) && <p className="text-[10px] text-slate-400 mt-0.5">= {platformPrice(periodForm.pm_booking)}/giorno</p>}
                  </Field>
                  <Field label="Airbnb (%)">
                    <NumInput value={periodForm.pm_airbnb} onChange={(e) => setPeriodForm(f => ({ ...f, pm_airbnb: e.target.value }))} data-testid="period-form-pm-airbnb" />
                    {platformPrice(periodForm.pm_airbnb) && <p className="text-[10px] text-slate-400 mt-0.5">= {platformPrice(periodForm.pm_airbnb)}/giorno</p>}
                  </Field>
                  <Field label="Expedia (%)">
                    <NumInput value={periodForm.pm_expedia} onChange={(e) => setPeriodForm(f => ({ ...f, pm_expedia: e.target.value }))} data-testid="period-form-pm-expedia" />
                    {platformPrice(periodForm.pm_expedia) && <p className="text-[10px] text-slate-400 mt-0.5">= {platformPrice(periodForm.pm_expedia)}/giorno</p>}
                  </Field>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Sogg. min (notti)"><NumInput step="1" value={periodForm.min_stay} onChange={(e) => setPeriodForm(f => ({ ...f, min_stay: e.target.value }))} data-testid="period-form-minstay" /></Field>
                <Field label="Anticipo min"><TextInput value={periodForm.min_advance} onChange={(e) => setPeriodForm(f => ({ ...f, min_advance: e.target.value }))} placeholder="2DOH" data-testid="period-form-advance" /></Field>
                <Field label="Fascia"><TextInput value={periodForm.band} onChange={(e) => setPeriodForm(f => ({ ...f, band: e.target.value }))} placeholder="A" maxLength={3} data-testid="period-form-band" /></Field>
                <Field label="Tipo">
                  <select value={periodForm.source} onChange={(e) => setPeriodForm(f => ({ ...f, source: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="period-form-source">
                    <option value="manual">Manuale</option>
                    <option value="auto_max">Auto prezzo max</option>
                    <option value="auto_annual">Auto annuale</option>
                    <option value="discount">Sconto</option>
                  </select>
                </Field>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { setPeriodDialog(false); setEditPeriod(null); }}>Annulla</Button>
                <Button type="submit" size="sm" disabled={periodSaving} data-testid="period-form-submit">
                  {periodSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  {editPeriod ? 'Salva modifiche' : 'Aggiungi'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
